/**
 * Onboarding Detail View
 * Step-by-step workflow management with timeline, checklists, and blockers
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  IconButton,
  Progress,
  Spinner,
  Text,
  useToast,
  VStack,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Checkbox,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Select,
  Input,
  RadioGroup,
  Radio,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdRadioButtonUnchecked,
  MdPlayArrow,
  MdCheck,
  MdSkipNext,
  MdWarning,
  MdNoteAdd,
  MdEmail,
  MdPhone,
  MdStar,
  MdStarBorder,
  MdArrowBack,
} from 'react-icons/md';
import {
  getOnboarding,
  startStep,
  completeStep,
  skipStep,
  blockStep,
  resolveBlocker,
  updateStepChecklist,
  addOnboardingActivity,
  captureNPS,
  captureSatisfaction,
} from '../../../services/onboardingManagement';
import type {
  Onboarding,
  OnboardingStep,
  OnboardingStepDetail,
  OnboardingBlocker,
  OnboardingActivity,
  ChecklistItem,
  StepStatus,
  BlockerSeverity,
  ActivityType,
} from '../../../types/onboarding';
import {
  getStepDisplayName,
  getStepOrder,
  getStepStatusColor,
  getBlockerSeverityColor,
} from '../../../types/onboarding';
import { useAuth } from '../../../contexts/AuthContext';

export function OnboardingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [selectedStep, setSelectedStep] = useState<OnboardingStep | null>(null);

  // Modals
  const { isOpen: isStartOpen, onOpen: onStartOpen, onClose: onStartClose } = useDisclosure();
  const { isOpen: isCompleteOpen, onOpen: onCompleteOpen, onClose: onCompleteClose } = useDisclosure();
  const { isOpen: isSkipOpen, onOpen: onSkipOpen, onClose: onSkipClose } = useDisclosure();
  const { isOpen: isBlockOpen, onOpen: onBlockOpen, onClose: onBlockClose } = useDisclosure();
  const { isOpen: isResolveOpen, onOpen: onResolveOpen, onClose: onResolveClose } = useDisclosure();
  const { isOpen: isNPSOpen, onOpen: onNPSOpen, onClose: onNPSClose } = useDisclosure();
  const { isOpen: isSatisfactionOpen, onOpen: onSatisfactionOpen, onClose: onSatisfactionClose } = useDisclosure();
  const { isOpen: isActivityOpen, onOpen: onActivityOpen, onClose: onActivityClose } = useDisclosure();

  // Form state
  const [startNotes, setStartNotes] = useState('');
  const [completeNotes, setCompleteNotes] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [blockerReason, setBlockerReason] = useState('');
  const [blockerSeverity, setBlockerSeverity] = useState<BlockerSeverity>('medium');
  const [selectedBlocker, setSelectedBlocker] = useState<OnboardingBlocker | null>(null);
  const [resolution, setResolution] = useState('');
  const [npsScore, setNpsScore] = useState<number | null>(null);
  const [npsComment, setNpsComment] = useState('');
  const [satisfactionRating, setSatisfactionRating] = useState<number | null>(null);
  const [satisfactionFeedback, setSatisfactionFeedback] = useState('');
  const [activityType, setActivityType] = useState<ActivityType>('note');
  const [activitySubject, setActivitySubject] = useState('');
  const [activityDescription, setActivityDescription] = useState('');

  // Load onboarding
  const loadOnboarding = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await getOnboarding(id);
      setOnboarding(data);
    } catch (error) {
      toast({
        title: 'Error loading onboarding',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
      navigate('/admin/onboarding');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOnboarding();
  }, [id]);

  if (loading || !onboarding) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  // Get step detail
  const getStepDetail = (step: OnboardingStep): OnboardingStepDetail | undefined => {
    return onboarding.steps.find(s => s.step === step);
  };

  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Format timestamp
  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  // Handle start step
  const handleStartStep = async () => {
    if (!selectedStep || !user) return;

    try {
      await startStep(id!, selectedStep, user.uid, user.displayName || 'User');
      
      if (startNotes) {
        await addOnboardingActivity(
          id!,
          'note',
          `Started ${getStepDisplayName(selectedStep)}`,
          startNotes,
          user.uid,
          user.displayName || 'User'
        );
      }

      toast({
        title: 'Step started',
        description: `${getStepDisplayName(selectedStep)} is now in progress`,
        status: 'success',
        duration: 3000,
      });

      onStartClose();
      setStartNotes('');
      setSelectedStep(null);
      loadOnboarding();
    } catch (error) {
      toast({
        title: 'Error starting step',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle complete step
  const handleCompleteStep = async () => {
    if (!selectedStep || !user) return;

    try {
      await completeStep(id!, selectedStep, user.uid, user.displayName || 'User', completeNotes);

      toast({
        title: 'Step completed',
        description: `${getStepDisplayName(selectedStep)} has been completed`,
        status: 'success',
        duration: 3000,
      });

      onCompleteClose();
      setCompleteNotes('');
      setSelectedStep(null);
      loadOnboarding();
    } catch (error) {
      toast({
        title: 'Error completing step',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle skip step
  const handleSkipStep = async () => {
    if (!selectedStep || !user) return;

    try {
      await skipStep(id!, selectedStep, skipReason, user.uid, user.displayName || 'User');

      toast({
        title: 'Step skipped',
        description: `${getStepDisplayName(selectedStep)} has been skipped`,
        status: 'success',
        duration: 3000,
      });

      onSkipClose();
      setSkipReason('');
      setSelectedStep(null);
      loadOnboarding();
    } catch (error) {
      toast({
        title: 'Error skipping step',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle block step
  const handleBlockStep = async () => {
    if (!selectedStep || !user) return;

    try {
      await blockStep(
        id!,
        selectedStep,
        blockerReason,
        blockerSeverity,
        user.uid,
        user.displayName || 'User'
      );

      toast({
        title: 'Blocker reported',
        description: `${getStepDisplayName(selectedStep)} is now blocked`,
        status: 'warning',
        duration: 3000,
      });

      onBlockClose();
      setBlockerReason('');
      setBlockerSeverity('medium');
      setSelectedStep(null);
      loadOnboarding();
    } catch (error) {
      toast({
        title: 'Error reporting blocker',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle resolve blocker
  const handleResolveBlocker = async () => {
    if (!selectedBlocker || !user) return;

    try {
      await resolveBlocker(
        id!,
        selectedBlocker.id,
        resolution,
        user.uid,
        user.displayName || 'User'
      );

      toast({
        title: 'Blocker resolved',
        description: 'The blocker has been resolved',
        status: 'success',
        duration: 3000,
      });

      onResolveClose();
      setResolution('');
      setSelectedBlocker(null);
      loadOnboarding();
    } catch (error) {
      toast({
        title: 'Error resolving blocker',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle checklist update
  const handleChecklistToggle = async (
    step: OnboardingStep,
    itemId: string,
    isCompleted: boolean,
    notes?: string
  ) => {
    if (!user) return;

    try {
      await updateStepChecklist(
        id!,
        step,
        itemId,
        isCompleted,
        user.uid,
        user.displayName || 'User',
        notes
      );

      loadOnboarding();
    } catch (error) {
      toast({
        title: 'Error updating checklist',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Handle capture NPS
  const handleCaptureNPS = async () => {
    if (npsScore === null || !user) return;

    try {
      await captureNPS(id!, npsScore, npsComment, user.uid, user.displayName || 'User');

      toast({
        title: 'NPS captured',
        description: 'Thank you for the feedback',
        status: 'success',
        duration: 3000,
      });

      onNPSClose();
      setNpsScore(null);
      setNpsComment('');
      loadOnboarding();
    } catch (error) {
      toast({
        title: 'Error capturing NPS',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle capture satisfaction
  const handleCaptureSatisfaction = async () => {
    if (satisfactionRating === null || !user) return;

    try {
      await captureSatisfaction(
        id!,
        satisfactionRating,
        satisfactionFeedback,
        user.uid,
        user.displayName || 'User'
      );

      toast({
        title: 'Satisfaction captured',
        description: 'Thank you for the feedback',
        status: 'success',
        duration: 3000,
      });

      onSatisfactionClose();
      setSatisfactionRating(null);
      setSatisfactionFeedback('');
      loadOnboarding();
    } catch (error) {
      toast({
        title: 'Error capturing satisfaction',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle add activity
  const handleAddActivity = async () => {
    if (!activitySubject || !user) return;

    try {
      await addOnboardingActivity(
        id!,
        activityType,
        activitySubject,
        activityDescription,
        user.uid,
        user.displayName || 'User'
      );

      toast({
        title: 'Activity added',
        status: 'success',
        duration: 2000,
      });

      onActivityClose();
      setActivityType('note');
      setActivitySubject('');
      setActivityDescription('');
      loadOnboarding();
    } catch (error) {
      toast({
        title: 'Error adding activity',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Get active blockers for a step
  const getActiveBlockers = (step: OnboardingStep): OnboardingBlocker[] => {
    return onboarding.blockers.filter(b => b.step === step && !b.resolvedAt);
  };

  // Get status icon
  const getStatusIcon = (status: StepStatus) => {
    switch (status) {
      case 'completed':
        return MdCheckCircle;
      case 'in_progress':
        return MdPlayArrow;
      case 'blocked':
        return MdWarning;
      case 'skipped':
        return MdSkipNext;
      default:
        return MdRadioButtonUnchecked;
    }
  };

  // Get activity icon
  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
      case 'call':
        return MdPhone;
      case 'email':
        return MdEmail;
      case 'note':
        return MdNoteAdd;
      case 'milestone':
        return MdCheckCircle;
      case 'blocker':
        return MdWarning;
      default:
        return MdNoteAdd;
    }
  };

  // Render NPS stars
  const renderNPSScore = () => {
    if (!onboarding.npsScore) return null;

    const score = onboarding.npsScore;
    const category = score >= 9 ? 'promoter' : score >= 7 ? 'passive' : 'detractor';
    const color = category === 'promoter' ? 'green' : category === 'passive' ? 'yellow' : 'red';

    return (
      <HStack>
        <Text fontWeight="bold" fontSize="2xl" color={`${color}.500`}>
          {score}/10
        </Text>
        <Badge colorScheme={color}>{category}</Badge>
      </HStack>
    );
  };

  // Render satisfaction stars
  const renderSatisfactionStars = () => {
    if (!onboarding.satisfactionRating) return null;

    return (
      <HStack>
        {[1, 2, 3, 4, 5].map((star) => (
          <Icon
            key={star}
            as={star <= onboarding.satisfactionRating! ? MdStar : MdStarBorder}
            color={star <= onboarding.satisfactionRating! ? 'yellow.400' : 'gray.300'}
            boxSize={6}
          />
        ))}
      </HStack>
    );
  };

  const steps: OnboardingStep[] = [
    'kickoff',
    'gmail_setup',
    'property_import',
    'tenant_import',
    'training',
    'testing',
    'go_live',
    'follow_up',
  ];

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <IconButton
            icon={<MdArrowBack />}
            aria-label="Back to queue"
            onClick={() => navigate('/admin/onboarding')}
            variant="ghost"
          />
          <VStack align="start" spacing={0}>
            <Heading size="lg">{onboarding.customerName}</Heading>
            <Text color="gray.600">{onboarding.companyName}</Text>
          </VStack>
        </HStack>
        <HStack spacing={4}>
          <Button
            leftIcon={<Icon as={MdNoteAdd} />}
            onClick={onActivityOpen}
          >
            Add Activity
          </Button>
          <Badge
            colorScheme={
              onboarding.status === 'completed' ? 'green' :
              onboarding.status === 'in_progress' ? 'blue' :
              onboarding.status === 'blocked' ? 'red' :
              onboarding.status === 'cancelled' ? 'orange' : 'gray'
            }
            fontSize="md"
            px={3}
            py={1}
          >
            {onboarding.status.replace('_', ' ').toUpperCase()}
          </Badge>
        </HStack>
      </Flex>

      <Grid templateColumns="2fr 1fr" gap={6}>
        {/* Main Content */}
        <VStack spacing={6} align="stretch">
          {/* Progress Card */}
          <Card>
            <CardBody>
              <Grid templateColumns="repeat(4, 1fr)" gap={6}>
                <Stat>
                  <StatLabel>Progress</StatLabel>
                  <StatNumber>{onboarding.progress}%</StatNumber>
                  <Progress
                    value={onboarding.progress}
                    colorScheme={onboarding.progress === 100 ? 'green' : 'blue'}
                    mt={2}
                  />
                </Stat>
                <Stat>
                  <StatLabel>Current Step</StatLabel>
                  <StatNumber fontSize="md">
                    {getStepDisplayName(onboarding.currentStep)}
                  </StatNumber>
                  <StatHelpText>
                    Step {getStepOrder(onboarding.currentStep)} of 8
                  </StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Specialist</StatLabel>
                  <StatNumber fontSize="md">
                    {onboarding.assignedToName || 'Unassigned'}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Target Date</StatLabel>
                  <StatNumber fontSize="md">
                    {formatDate(onboarding.targetCompletionDate)}
                  </StatNumber>
                </Stat>
              </Grid>
            </CardBody>
          </Card>

          {/* Timeline Visualization */}
          <Card>
            <CardHeader>
              <Heading size="md">Onboarding Timeline</Heading>
            </CardHeader>
            <CardBody>
              <HStack spacing={2} justify="space-between">
                {steps.map((step) => {
                  const detail = getStepDetail(step);
                  const StatusIcon = getStatusIcon(detail?.status || 'pending');
                  const color = getStepStatusColor(detail?.status || 'pending');
                  const isCurrent = onboarding.currentStep === step;

                  return (
                    <Tooltip
                      key={step}
                      label={`${getStepDisplayName(step)} - ${detail?.status || 'pending'}`}
                    >
                      <VStack spacing={1}>
                        <Icon
                          as={StatusIcon}
                          boxSize={isCurrent ? 8 : 6}
                          color={`${color}.500`}
                          animation={detail?.status === 'in_progress' ? 'pulse 2s infinite' : undefined}
                        />
                        <Text
                          fontSize={isCurrent ? 'sm' : 'xs'}
                          fontWeight={isCurrent ? 'bold' : 'normal'}
                          textAlign="center"
                          noOfLines={2}
                        >
                          {getStepDisplayName(step)}
                        </Text>
                      </VStack>
                    </Tooltip>
                  );
                })}
              </HStack>
            </CardBody>
          </Card>

          {/* Steps Accordion */}
          <Card>
            <CardHeader>
              <Heading size="md">Onboarding Steps</Heading>
            </CardHeader>
            <CardBody p={0}>
              <Accordion allowMultiple defaultIndex={steps.map((_, i) => i)}>
                {steps.map((step) => {
                  const detail = getStepDetail(step);
                  const activeBlockers = getActiveBlockers(step);
                  const isCurrent = onboarding.currentStep === step;

                  if (!detail) return null;

                  return (
                    <AccordionItem key={step}>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          <HStack spacing={3}>
                            <Icon
                              as={getStatusIcon(detail.status)}
                              color={`${getStepStatusColor(detail.status)}.500`}
                              boxSize={5}
                            />
                            <Text fontWeight={isCurrent ? 'bold' : 'normal'}>
                              {getStepDisplayName(step)}
                            </Text>
                            {activeBlockers.length > 0 && (
                              <Badge colorScheme="red">
                                {activeBlockers.length} blocker{activeBlockers.length > 1 ? 's' : ''}
                              </Badge>
                            )}
                            {detail.status === 'completed' && detail.durationDays && (
                              <Badge colorScheme="green">
                                {detail.durationDays} days
                              </Badge>
                            )}
                          </HStack>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <VStack align="stretch" spacing={4}>
                          {/* Step Info */}
                          {detail.startedAt && (
                            <Text fontSize="sm" color="gray.600">
                              Started: {formatTimestamp(detail.startedAt)}
                              {detail.completedAt && ` • Completed: ${formatTimestamp(detail.completedAt)}`}
                            </Text>
                          )}

                          {/* Active Blockers */}
                          {activeBlockers.length > 0 && (
                            <Alert status="error">
                              <AlertIcon />
                              <Box flex="1">
                                <AlertTitle>Active Blockers</AlertTitle>
                                <AlertDescription>
                                  <VStack align="stretch" mt={2} spacing={2}>
                                    {activeBlockers.map((blocker) => (
                                      <Card key={blocker.id} variant="outline">
                                        <CardBody>
                                          <HStack justify="space-between">
                                            <VStack align="start" spacing={1}>
                                              <Badge colorScheme={getBlockerSeverityColor(blocker.severity)}>
                                                {blocker.severity}
                                              </Badge>
                                              <Text fontSize="sm">{blocker.reason}</Text>
                                              <Text fontSize="xs" color="gray.600">
                                                Reported by {blocker.reportedByName} on {formatDate(blocker.reportedAt)}
                                              </Text>
                                            </VStack>
                                            <Button
                                              size="sm"
                                              colorScheme="green"
                                              onClick={() => {
                                                setSelectedBlocker(blocker);
                                                onResolveOpen();
                                              }}
                                            >
                                              Resolve
                                            </Button>
                                          </HStack>
                                        </CardBody>
                                      </Card>
                                    ))}
                                  </VStack>
                                </AlertDescription>
                              </Box>
                            </Alert>
                          )}

                          {/* Checklist */}
                          <Box>
                            <Heading size="sm" mb={3}>Checklist</Heading>
                            <VStack align="stretch" spacing={2}>
                              {detail.checklist.map((item: ChecklistItem) => (
                                <Card key={item.id} variant="outline">
                                  <CardBody>
                                    <HStack spacing={3}>
                                      <Checkbox
                                        isChecked={item.isCompleted}
                                        onChange={(e) =>
                                          handleChecklistToggle(
                                            step,
                                            item.id,
                                            e.target.checked
                                          )
                                        }
                                        isDisabled={detail.status === 'completed' || detail.status === 'skipped'}
                                      />
                                      <VStack align="start" flex="1" spacing={0}>
                                        <HStack>
                                          <Text
                                            fontSize="sm"
                                            textDecoration={item.isCompleted ? 'line-through' : 'none'}
                                          >
                                            {item.description}
                                          </Text>
                                          {item.isRequired && (
                                            <Badge colorScheme="red" fontSize="xs">
                                              Required
                                            </Badge>
                                          )}
                                        </HStack>
                                        {item.isCompleted && item.completedByName && (
                                          <Text fontSize="xs" color="gray.600">
                                            Completed by {item.completedByName} on {formatDate(item.completedAt)}
                                          </Text>
                                        )}
                                      </VStack>
                                    </HStack>
                                  </CardBody>
                                </Card>
                              ))}
                            </VStack>
                          </Box>

                          {/* Step Notes */}
                          {detail.notes && (
                            <Box>
                              <Heading size="sm" mb={2}>Notes</Heading>
                              <Card variant="outline">
                                <CardBody>
                                  <Text fontSize="sm" whiteSpace="pre-wrap">
                                    {detail.notes}
                                  </Text>
                                </CardBody>
                              </Card>
                            </Box>
                          )}

                          {/* Step Actions */}
                          <HStack spacing={2}>
                            {detail.status === 'pending' && (
                              <Button
                                leftIcon={<MdPlayArrow />}
                                colorScheme="blue"
                                size="sm"
                                onClick={() => {
                                  setSelectedStep(step);
                                  onStartOpen();
                                }}
                              >
                                Start Step
                              </Button>
                            )}
                            {detail.status === 'in_progress' && (
                              <>
                                <Button
                                  leftIcon={<MdCheck />}
                                  colorScheme="green"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedStep(step);
                                    onCompleteOpen();
                                  }}
                                >
                                  Mark Complete
                                </Button>
                                <Button
                                  leftIcon={<MdWarning />}
                                  colorScheme="orange"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedStep(step);
                                    onBlockOpen();
                                  }}
                                >
                                  Report Blocker
                                </Button>
                                <Button
                                  leftIcon={<MdSkipNext />}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedStep(step);
                                    onSkipOpen();
                                  }}
                                >
                                  Skip Step
                                </Button>
                              </>
                            )}
                          </HStack>
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </CardBody>
          </Card>
        </VStack>

        {/* Sidebar */}
        <VStack spacing={6} align="stretch">
          {/* Customer Feedback */}
          <Card>
            <CardHeader>
              <Heading size="md">Customer Feedback</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                {/* NPS Score */}
                <Box>
                  <Text fontWeight="bold" mb={2}>NPS Score</Text>
                  {onboarding.npsScore ? (
                    <VStack align="start" spacing={2}>
                      {renderNPSScore()}
                      {onboarding.feedback && (
                        <Text fontSize="sm" color="gray.600">
                          "{onboarding.feedback}"
                        </Text>
                      )}
                    </VStack>
                  ) : (
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={onNPSOpen}
                    >
                      Request NPS
                    </Button>
                  )}
                </Box>

                <Divider />

                {/* Satisfaction Rating */}
                <Box>
                  <Text fontWeight="bold" mb={2}>Satisfaction</Text>
                  {onboarding.satisfactionRating ? (
                    <VStack align="start" spacing={2}>
                      {renderSatisfactionStars()}
                      {onboarding.feedback && (
                        <Text fontSize="sm" color="gray.600">
                          "{onboarding.feedback}"
                        </Text>
                      )}
                    </VStack>
                  ) : (
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={onSatisfactionOpen}
                    >
                      Request Feedback
                    </Button>
                  )}
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Activity Timeline */}
          <Card>
            <CardHeader>
              <Heading size="md">Activity Timeline</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={3} maxH="500px" overflowY="auto">
                {onboarding.activities.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">No activities yet</Text>
                ) : (
                  onboarding.activities
                    .sort((a, b) => b.timestamp.toDate().getTime() - a.timestamp.toDate().getTime())
                    .map((activity: OnboardingActivity) => (
                      <Card key={activity.id} variant="outline" size="sm">
                        <CardBody>
                          <HStack spacing={2} align="start">
                            <Icon
                              as={getActivityIcon(activity.type)}
                              color="blue.500"
                              mt={1}
                            />
                            <VStack align="start" spacing={0} flex="1">
                              <Text fontSize="sm" fontWeight="bold">
                                {activity.subject}
                              </Text>
                              {activity.description && (
                                <Text fontSize="sm" color="gray.600">
                                  {activity.description}
                                </Text>
                              )}
                              <Text fontSize="xs" color="gray.500">
                                {activity.performedByName} • {formatTimestamp(activity.timestamp)}
                              </Text>
                            </VStack>
                          </HStack>
                        </CardBody>
                      </Card>
                    ))
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Grid>

      {/* Modals */}
      
      {/* Start Step Modal */}
      <Modal isOpen={isStartOpen} onClose={onStartClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Start {selectedStep ? getStepDisplayName(selectedStep) : 'Step'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Notes (optional)</FormLabel>
              <Textarea
                value={startNotes}
                onChange={(e) => setStartNotes(e.target.value)}
                placeholder="Add any notes about starting this step..."
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onStartClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleStartStep}>
              Start Step
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Complete Step Modal */}
      <Modal isOpen={isCompleteOpen} onClose={onCompleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Complete {selectedStep ? getStepDisplayName(selectedStep) : 'Step'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Completion Notes (optional)</FormLabel>
              <Textarea
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
                placeholder="Add any notes about completing this step..."
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCompleteClose}>
              Cancel
            </Button>
            <Button colorScheme="green" onClick={handleCompleteStep}>
              Mark Complete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Skip Step Modal */}
      <Modal isOpen={isSkipOpen} onClose={onSkipClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Skip {selectedStep ? getStepDisplayName(selectedStep) : 'Step'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Reason for Skipping</FormLabel>
              <Textarea
                value={skipReason}
                onChange={(e) => setSkipReason(e.target.value)}
                placeholder="Explain why this step is being skipped..."
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onSkipClose}>
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              onClick={handleSkipStep}
              isDisabled={!skipReason}
            >
              Skip Step
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Report Blocker Modal */}
      <Modal isOpen={isBlockOpen} onClose={onBlockClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Report Blocker for {selectedStep ? getStepDisplayName(selectedStep) : 'Step'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Blocker Description</FormLabel>
                <Textarea
                  value={blockerReason}
                  onChange={(e) => setBlockerReason(e.target.value)}
                  placeholder="Describe the blocker..."
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Severity</FormLabel>
                <RadioGroup value={blockerSeverity} onChange={(v) => setBlockerSeverity(v as BlockerSeverity)}>
                  <Stack>
                    <Radio value="low">Low - Minor issue, no immediate impact</Radio>
                    <Radio value="medium">Medium - Needs attention soon</Radio>
                    <Radio value="high">High - Blocking progress</Radio>
                    <Radio value="critical">Critical - Urgent, major impact</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onBlockClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleBlockStep}
              isDisabled={!blockerReason}
            >
              Report Blocker
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Resolve Blocker Modal */}
      <Modal isOpen={isResolveOpen} onClose={onResolveClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Resolve Blocker</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {selectedBlocker && (
                <Card variant="outline">
                  <CardBody>
                    <VStack align="start" spacing={2}>
                      <Badge colorScheme={getBlockerSeverityColor(selectedBlocker.severity)}>
                        {selectedBlocker.severity}
                      </Badge>
                      <Text fontSize="sm">{selectedBlocker.reason}</Text>
                    </VStack>
                  </CardBody>
                </Card>
              )}
              <FormControl isRequired>
                <FormLabel>Resolution Details</FormLabel>
                <Textarea
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  placeholder="Describe how the blocker was resolved..."
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onResolveClose}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleResolveBlocker}
              isDisabled={!resolution}
            >
              Resolve Blocker
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Capture NPS Modal */}
      <Modal isOpen={isNPSOpen} onClose={onNPSClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Capture NPS Score</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>NPS Score (0-10)</FormLabel>
                <HStack spacing={2}>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                    <Button
                      key={score}
                      size="sm"
                      colorScheme={npsScore === score ? 'blue' : 'gray'}
                      onClick={() => setNpsScore(score)}
                    >
                      {score}
                    </Button>
                  ))}
                </HStack>
              </FormControl>
              <FormControl>
                <FormLabel>Feedback (optional)</FormLabel>
                <Textarea
                  value={npsComment}
                  onChange={(e) => setNpsComment(e.target.value)}
                  placeholder="Additional comments..."
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onNPSClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCaptureNPS}
              isDisabled={npsScore === null}
            >
              Save NPS
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Capture Satisfaction Modal */}
      <Modal isOpen={isSatisfactionOpen} onClose={onSatisfactionClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Capture Satisfaction Rating</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Rating (1-5 stars)</FormLabel>
                <HStack spacing={2}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <IconButton
                      key={rating}
                      icon={<Icon as={rating <= (satisfactionRating || 0) ? MdStar : MdStarBorder} />}
                      aria-label={`${rating} stars`}
                      onClick={() => setSatisfactionRating(rating)}
                      colorScheme={rating <= (satisfactionRating || 0) ? 'yellow' : 'gray'}
                      size="lg"
                    />
                  ))}
                </HStack>
              </FormControl>
              <FormControl>
                <FormLabel>Feedback (optional)</FormLabel>
                <Textarea
                  value={satisfactionFeedback}
                  onChange={(e) => setSatisfactionFeedback(e.target.value)}
                  placeholder="Additional feedback..."
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onSatisfactionClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCaptureSatisfaction}
              isDisabled={satisfactionRating === null}
            >
              Save Rating
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Activity Modal */}
      <Modal isOpen={isActivityOpen} onClose={onActivityClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Activity</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Activity Type</FormLabel>
                <Select
                  value={activityType}
                  onChange={(e) => setActivityType(e.target.value as ActivityType)}
                >
                  <option value="note">Note</option>
                  <option value="call">Phone Call</option>
                  <option value="email">Email</option>
                  <option value="meeting">Meeting</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Subject</FormLabel>
                <Input
                  value={activitySubject}
                  onChange={(e) => setActivitySubject(e.target.value)}
                  placeholder="Activity subject..."
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={activityDescription}
                  onChange={(e) => setActivityDescription(e.target.value)}
                  placeholder="Activity details..."
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onActivityClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleAddActivity}
              isDisabled={!activitySubject}
            >
              Add Activity
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
